import React, { Fragment, useCallback, useEffect, useState } from 'react';
import moment from 'moment';

import { CREATE, GET_LIST, provider, history } from '../_utils';
import { SearchClear, PcSelect } from '../_Components';
import '../operators/operators.css';
import './projects.css';

const SORT_FIELD = {
    name: 'name',
    updated: 'updateAt',
    status: 'status',
};

const CRM_META = {
    Bitrix: { label: 'Bitrix', chip: 'pc-emp-chip--acc' },
    Amocrm: { label: 'AmoCRM', chip: 'pc-emp-chip--vio' },
    None: { label: 'нет', chip: '' },
};

const statusMeta = (row) => (
    row?.status === 'Active'
        ? { label: 'Активен', chip: 'pc-emp-chip--ok' }
        : { label: 'Отключён', chip: 'pc-emp-chip--bad' }
);

const countOf = (value) => {
    if (Array.isArray(value)) return value.length;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const initials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '—';
    return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

const fmtStamp = (value) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '');

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const CreateProjectModal = ({ open, onClose, onCreated }) => {
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        setName('');
        setSaving(false);
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const handleSave = async () => {
        const value = name.trim();
        if (!value || saving) return;
        setSaving(true);
        try {
            const result = await provider(CREATE, '/projects', { data: { name: value } });
            if (result.status === 200) {
                const id = result.data?._id || result.data?.id;
                onClose();
                if (id) history.push(`/projects/${id}`);
                else if (onCreated) onCreated();
            }
        } catch (error) {
            console.error('Error creating project:', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pc-emp-ov" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="pc-emp-modal pc-emp-modal--sm" role="dialog" aria-modal="true" aria-labelledby="pc-proj-create-title">
                <div className="pc-emp-mh">
                    <div>
                        <h3 id="pc-proj-create-title">Новый проект</h3>
                        <div className="sub">Название можно изменить позже</div>
                    </div>
                    <button type="button" className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet" onClick={onClose} title="Закрыть">
                        <Icon d="M4 4l8 8M12 4l-8 8" />
                    </button>
                </div>
                <div className="pc-emp-mb">
                    <label className="pc-emp-fl">
                        <span>Название</span>
                        <input
                            className="pc-emp-inp"
                            value={name}
                            autoFocus
                            onChange={(event) => setName(event.target.value)}
                            onKeyDown={(event) => { if (event.key === 'Enter') handleSave(); }}
                        />
                    </label>
                </div>
                <div className="pc-emp-mf">
                    <button type="button" className="pc-emp-btn pc-emp-btn--pri" disabled={saving || !name.trim()} onClick={handleSave}>
                        Создать
                    </button>
                    <button type="button" className="pc-emp-btn" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
};

const ProjectList = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [act, setAct] = useState('Active');
    const [sortKey, setSortKey] = useState('updated');
    const [sortDir, setSortDir] = useState(-1);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [openCreate, setOpenCreate] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const load = useCallback(async () => {
        const filter = {};
        if (act) filter.status = act;
        if (debouncedQuery) filter.name = debouncedQuery;

        try {
            const { data, total: count } = await provider(GET_LIST, '/projects', {
                filter,
                sort: { field: SORT_FIELD[sortKey] || 'updateAt', order: sortDir > 0 ? 'ASC' : 'DESC' },
                pagination: { page, perPage },
            });
            setRows(data || []);
            setTotal(count || 0);
        } catch (error) {
            console.error('Error loading projects:', error);
        }
    }, [act, debouncedQuery, sortKey, sortDir, page, perPage]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [act, debouncedQuery, sortKey, sortDir, perPage]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir((dir) => -dir);
        else { setSortKey(key); setSortDir(key === 'name' ? 1 : -1); }
    };

    const sortClass = (key) => (sortKey === key ? 's' : '');
    const arrow = sortDir > 0 ? '↑' : '↓';
    const pages = Math.max(1, Math.ceil(total / perPage));

    return (
        <Fragment>
            <div className="pc-page pc-emp pc-proj">
                <div className="pc-emp-topbar">
                    <h1>Проекты</h1>
                    <div className="pc-emp-srch">
                        <Icon d="M7 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zM10.5 10.5L14 14" />
                        <input
                            className="pc-emp-inp"
                            value={query}
                            placeholder="Поиск по названию"
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <SearchClear visible={Boolean(query)} onClear={() => setQuery('')} />
                    </div>
                    <PcSelect
                        value={act}
                        onChange={(e) => setAct(e.target.value)}
                        options={[
                            { id: 'Active', name: 'Активные' },
                            { id: 'Disabled', name: 'Отключённые' },
                        ]}
                        allowEmpty
                        emptyLabel="Все статусы"
                        searchable={false}
                    />
                    <span className="pc-emp-chip pc-emp-chip--mono">
                        {total
                            ? `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} из ${total}`
                            : '0 проектов'}
                    </span>
                    <div className="pc-emp-end">
                        <button type="button" className="pc-emp-btn pc-emp-btn--pri" onClick={() => setOpenCreate(true)}>
                            <Icon d="M8 3.5v9M3.5 8h9" />
                            Новый проект
                        </button>
                    </div>
                </div>

                <div className="pc-emp-stage">
                    <div className="pc-emp-panel">
                        <div className="pc-emp-thead">
                            <span className={`pc-proj-c-name ${sortClass('name')}`} data-sort="name" onClick={() => toggleSort('name')}>
                                Проект<span className="ar">{arrow}</span>
                            </span>
                            <span className="pc-proj-c-crm">CRM</span>
                            <span className="pc-proj-c-num">Компании</span>
                            <span className="pc-proj-c-num">Карточки</span>
                            <span className="pc-proj-c-num">Поля</span>
                            <span className="pc-proj-c-num">Отчёты</span>
                            <span className={`pc-proj-c-upd ${sortClass('updated')}`} data-sort="updated" onClick={() => toggleSort('updated')}>
                                Обновлён<span className="ar">{arrow}</span>
                            </span>
                            <span className="pc-proj-c-user">Изменил</span>
                            <span className={`pc-proj-c-st ${sortClass('status')}`} data-sort="status" onClick={() => toggleSort('status')}>
                                Статус<span className="ar">{arrow}</span>
                            </span>
                            <span className="pc-emp-c-go" />
                        </div>
                        <div className="pc-emp-pbd">
                            {rows.length === 0 ? (
                                <div className="pc-emp-empty">
                                    <b>Ничего не найдено</b>
                                    <p>Измените запрос или сбросьте фильтры.</p>
                                </div>
                            ) : rows.map((row) => {
                                const id = row._id || row.id;
                                const status = statusMeta(row);
                                const crm = CRM_META[row.crm] || CRM_META.None;
                                const active = row.status === 'Active';
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        className={`pc-emp-trow${active ? '' : ' off'}`}
                                        onClick={() => history.push(`/projects/${id}`)}
                                    >
                                        <span className="pc-proj-c-name">
                                            <span className="pc-emp-av pc-emp-av-operator">{initials(row.name)}</span>
                                            <span style={{ minWidth: 0 }}>
                                                <span className="n">{row.name || '—'}</span>
                                                {row.customer ? <span className="s">{row.customer}</span> : null}
                                            </span>
                                        </span>
                                        <span className="pc-proj-c-crm">
                                            <span className={`pc-emp-chip ${crm.chip}`}>{crm.label}</span>
                                        </span>
                                        <span className="pc-proj-c-num">{countOf(row.campaigns)}</span>
                                        <span className="pc-proj-c-num">{countOf(row.cards)}</span>
                                        <span className="pc-proj-c-num">{countOf(row.fields)}</span>
                                        <span className="pc-proj-c-num">{countOf(row.reports)}</span>
                                        <span className="pc-proj-c-upd">
                                            {row.updateAt ? fmtStamp(row.updateAt) : <span className="pc-emp-dim">—</span>}
                                        </span>
                                        <span className="pc-proj-c-user">
                                            {row.modified && row.modified !== '-'
                                                ? row.modified
                                                : <span className="pc-emp-dim">—</span>}
                                        </span>
                                        <span className="pc-proj-c-st">
                                            <span className={`pc-emp-chip ${status.chip}`}>
                                                <span className="pc-emp-dot" />
                                                {status.label}
                                            </span>
                                        </span>
                                        <span className="pc-emp-c-go">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                                <path d="M6 3.5L10.5 8 6 12.5" />
                                            </svg>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="pc-emp-pager">
                            <PcSelect
                                value={String(perPage)}
                                onChange={(e) => setPerPage(Number(e.target.value))}
                                options={[25, 50, 100].map((n) => ({ id: String(n), name: `${n} на странице` }))}
                                searchable={false}
                            />
                            <span className="pc-emp-pager__n">{page} / {pages}</span>
                            <div className="pc-emp-navb">
                                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title="Назад">
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M10 3.5L5.5 8l4.5 4.5" />
                                    </svg>
                                </button>
                                <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} title="Вперёд">
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M6 3.5L10.5 8 6 12.5" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CreateProjectModal
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onCreated={load}
            />
        </Fragment>
    );
};

export default ProjectList;
